// Dock-side subscriber for `BUG_REPORT_PANEL_STATE_EVENT`. Resolves
// the "auto-collapse on bug-report open" open follow-up from
// docs/planning/bug_report_placement_v2.md ("Likely yes for desktop").
//
// Discipline mirrors `useActivityDockKeyboardCollapse`:
//   1. Edge-triggered close only (we never auto-re-open on the
//      panel's close event — user-initiated only).
//   2. Idempotent close — calling the supplied `closePanel` when the
//      dock is already closed is a no-op the caller absorbs.
//   3. Gated on event source via the pure
//      `shouldCollapseDockOnBugReportOpen` predicate so the mobile
//      drawer (which fully occludes the dock anyway) doesn't churn
//      the dock open-state unnecessarily.

import { useEffect } from "react";
import {
  BUG_REPORT_PANEL_STATE_EVENT,
  shouldCollapseDockOnBugReportOpen,
  type BugReportPanelStateEventDetail,
} from "@/lib/bugReportPanelOpenEvent";

export function useActivityDockCollapseOnBugReport(
  closePanel: () => void,
): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onPanelState(evt: Event) {
      const detail = (evt as CustomEvent<BugReportPanelStateEventDetail>).detail;
      if (!detail) return;
      if (!shouldCollapseDockOnBugReportOpen(detail)) return;
      closePanel();
    }
    window.addEventListener(BUG_REPORT_PANEL_STATE_EVENT, onPanelState);
    return () =>
      window.removeEventListener(BUG_REPORT_PANEL_STATE_EVENT, onPanelState);
  }, [closePanel]);
}
